import { useRef, useEffect, useLayoutEffect, useCallback, useState } from "react";
import { ChatMessage, CommunicationUserIdentifier } from "../types/chat";

interface UseVirtualizedMessagesOptions {
  messages: ChatMessage[];
  threadId?: string;
  currentUserId?: string;
  onLoadMore?: () => Promise<any> | void;
  hasMore?: boolean;
  isLoading?: boolean;
}

export const useVirtualizedMessages = ({
  messages,
  threadId,
  currentUserId,
  onLoadMore,
  hasMore = false,
  isLoading = false,
}: UseVirtualizedMessagesOptions) => {
  /* ------------------------------------------------
   * State + refs
   * ------------------------------------------------ */
  const containerRef = useRef<HTMLDivElement>(null);

  const isInitialLoadRef = useRef(true);
  const isAtBottomRef = useRef(true);
  const isLoadingMoreRef = useRef(false);

  const prevMessageCountRef = useRef(0);
  const lastMessageIdRef = useRef<string | null>(null);

  const anchorIdRef = useRef<string | null>(null);
  const anchorOffsetRef = useRef(0);

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  /* ------------------------------------------------
   * Helper: check if input is focused on MOBILE (keyboard likely open)
   * On desktop, always return false to preserve normal scroll behavior
   * ------------------------------------------------ */
  const isInputFocused = useCallback(() => {
    // Only apply this check on mobile devices
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    if (!isMobile) return false; // Desktop: never skip scroll

    const activeEl = document.activeElement;
    return activeEl?.tagName === "INPUT" || activeEl?.tagName === "TEXTAREA";
  }, []);

  /* ------------------------------------------------
   * Helper: scroll bottom
   * ------------------------------------------------ */
  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = "auto") => {
      const el = containerRef.current;
      if (!el) return;
      // Skip scroll if input is focused to prevent keyboard dismissal
      if (isInputFocused()) return;
      el.scrollTop = el.scrollHeight;
      isAtBottomRef.current = true;
      setShowScrollButton(false);
      setUnreadCount(0);
    },
    [isInputFocused],
  );

  /* ------------------------------------------------
   * Reset khi đổi room
   * ------------------------------------------------ */
  useEffect(() => {
    isInitialLoadRef.current = true;
    isAtBottomRef.current = true;
    isLoadingMoreRef.current = false;

    prevMessageCountRef.current = 0;
    lastMessageIdRef.current = null;

    setUnreadCount(0);
    setShowScrollButton(false);
  }, [threadId]);

  /* ------------------------------------------------
   * STABILIZATION: After room loads, disable aggressive scroll
   * This prevents scroll from interfering with keyboard on mobile
   * ------------------------------------------------ */
  const isStabilizedRef = useRef(false);

  useEffect(() => {
    // Only on mobile
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    if (!isMobile) return;

    // Reset on room change
    isStabilizedRef.current = false;

    // Mark as stabilized after 1 second
    const timer = setTimeout(() => {
      isStabilizedRef.current = true;
    }, 1000);

    return () => clearTimeout(timer);
  }, [threadId]);

  /* ------------------------------------------------
   * INITIAL SCROLL – retry 3 frame
   * ------------------------------------------------ */
  useLayoutEffect(() => {
    if (!messages.length || !isInitialLoadRef.current) return;

    let frame = 0;
    const maxFrame = 3;

    const retryScroll = () => {
      scrollToBottom("auto");
      frame++;
      if (frame < maxFrame) requestAnimationFrame(retryScroll);
      else {
        isInitialLoadRef.current = false;
        prevMessageCountRef.current = messages.length;
        lastMessageIdRef.current = messages[messages.length - 1]?.id ?? null;
      }
    };

    requestAnimationFrame(retryScroll);
  }, [messages, scrollToBottom]);

  /* ------------------------------------------------
   * FIX MEDIA ASYNC – observe last message
   * ------------------------------------------------ */
  useEffect(() => {
    if (!isAtBottomRef.current) return;
    // Skip if input is focused
    if (isInputFocused()) return;

    const el = containerRef.current;
    if (!el) return;

    const nodes = el.querySelectorAll("[data-message-id]");
    const lastEl = nodes[nodes.length - 1] as HTMLElement;
    if (!lastEl) return;

    const obs = new ResizeObserver(() => {
      if (isAtBottomRef.current && !isInputFocused()) el.scrollTop = el.scrollHeight;
    });

    obs.observe(lastEl);
    return () => obs.disconnect();
  }, [messages.length, isInputFocused]);

  /* ------------------------------------------------
   * IMAGES LOAD FIX
   * ------------------------------------------------ */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    if (!isAtBottomRef.current) return;

    const imgs = el.querySelectorAll("img");
    imgs.forEach((img) => {
      if (!img.complete) {
        img.onload = () => {
          // Skip scroll if input is focused
          if (isAtBottomRef.current && !isInputFocused()) el.scrollTop = el.scrollHeight;
        };
      }
    });
  }, [messages.length, isInputFocused]);

  /* ------------------------------------------------
   * PRESERVE SCROLL – anchor restore
   * ------------------------------------------------ */
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // BEFORE LOAD
    if (isLoadingMoreRef.current && isLoading) {
      const nodes = el.querySelectorAll("[data-message-id]");
      for (const node of nodes) {
        const rect = (node as HTMLElement).getBoundingClientRect();
        const crect = el.getBoundingClientRect();
        if (rect.top >= crect.top) {
          anchorIdRef.current = node.getAttribute("data-message-id");
          anchorOffsetRef.current = rect.top - crect.top;
          break;
        }
      }
    }

    // AFTER LOAD
    if (isLoadingMoreRef.current && !isLoading) {
      const id = anchorIdRef.current;
      if (id) {
        const anchor = el.querySelector(`[data-message-id="${id}"]`) as HTMLElement;
        if (anchor) {
          const rect = anchor.getBoundingClientRect();
          const crect = el.getBoundingClientRect();
          const offset = rect.top - crect.top;
          el.scrollTop += offset - anchorOffsetRef.current;
        }
      }
      isLoadingMoreRef.current = false;
      anchorIdRef.current = null;
      anchorOffsetRef.current = 0;
    }
  }, [isLoading, messages.length]);

  /* ------------------------------------------------
   * KEYBOARD OPENING TRACKING (Mobile only)
   * Track when keyboard is opening to prevent blur during auto-scroll
   * ------------------------------------------------ */
  const isKeyboardOpeningRef = useRef(false);

  useEffect(() => {
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    if (!isMobile) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
        // Mark keyboard as opening
        isKeyboardOpeningRef.current = true;
        // Reset after keyboard animation completes (~500ms)
        setTimeout(() => {
          isKeyboardOpeningRef.current = false;
        }, 500);
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    return () => document.removeEventListener("focusin", handleFocusIn);
  }, []);

  /* ------------------------------------------------
   * SCROLL LISTENER
   * ------------------------------------------------ */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Check if mobile device
    const isMobile = window.innerWidth <= 768;

    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      const dist = scrollHeight - scrollTop - clientHeight;

      const atBottom = dist < 20;
      const nearBottom = dist < 500;

      isAtBottomRef.current = atBottom || nearBottom;
      setShowScrollButton(!isAtBottomRef.current);

      if (isAtBottomRef.current && unreadCount > 0) setUnreadCount(0);

      if (scrollTop < 150 && hasMore && !isLoading && !isLoadingMoreRef.current && onLoadMore) {
        isLoadingMoreRef.current = true;
        onLoadMore();
      }

      // Mobile: blur input on scroll to hide keyboard (like tapping outside)
      // BUT skip if keyboard is currently opening (browser auto-scroll)
      if (isMobile && !isKeyboardOpeningRef.current) {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && (activeElement.tagName === "TEXTAREA" || activeElement.tagName === "INPUT")) {
          activeElement.blur();
        }
      }
    };

    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, isLoading, onLoadMore, unreadCount]);

  /* ------------------------------------------------
   * NEW MESSAGE LOGIC
   * ------------------------------------------------ */
  useEffect(() => {
    if (isInitialLoadRef.current) return;
    if (!messages.length) return;

    const lastId = messages[messages.length - 1]?.id ?? null;
    const prevId = lastMessageIdRef.current;
    const newCount = messages.length - prevMessageCountRef.current;

    if (lastId !== prevId && newCount > 0) {
      const newest = messages.slice(-newCount);

      const own =
        currentUserId &&
        newest.some((m) => {
          const sender = m.senderCommunicationIdentifier as CommunicationUserIdentifier | string | undefined;
          if (typeof sender === "string") return sender === currentUserId;
          if (typeof sender === "object") return sender?.communicationUser?.id === currentUserId;
          return false;
        });

      if (own || isAtBottomRef.current) {
        requestAnimationFrame(() => scrollToBottom("smooth"));
      } else {
        setUnreadCount((n) => n + newCount);
      }
    }

    prevMessageCountRef.current = messages.length;
    lastMessageIdRef.current = lastId;
  }, [messages.length, scrollToBottom, currentUserId]);

  /* ------------------------------------------------
   * FINAL BOTTOM ENSURE – retry stabilizer
   * Skip on mobile after room is stabilized to prevent keyboard issues
   * ------------------------------------------------ */
  useEffect(() => {
    if (!isAtBottomRef.current) return;

    // On mobile, skip this after room is stabilized (avoids keyboard conflicts)
    const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
    if (isMobile && isStabilizedRef.current) return;

    // Also skip if input is focused
    if (isInputFocused()) return;

    const el = containerRef.current;
    if (!el) return;

    let frame = 0;
    const max = 5;

    const check = () => {
      // Double check before each frame
      if (isInputFocused()) return;
      if (isMobile && isStabilizedRef.current) return;
      el.scrollTop = el.scrollHeight;
      frame++;
      if (frame < max) requestAnimationFrame(check);
    };

    requestAnimationFrame(check);
  }, [messages.length, isInputFocused]);

  return {
    containerRef,
    scrollToBottom,
    showScrollButton,
    unreadCount,
  };
};
